/**
 * The app-side emit host for the practice trial-start modal and the team-invite
 * modal it opens (`@heidi/commerce`).
 *
 * Ruling 24: the domain side decides WHEN each event fires and builds every
 * `intl`-formatted label; these four functions add the `event_name`, the
 * destination routing, and nothing else.
 *
 * ROUTING IS PRESERVED, NOT TIDIED: both views pass `{ amplitude: false }` and
 * all five clicks pass none. Neither surface's event names are on the Snowplow
 * denylist (`@/lib/analytics/snowplow-suppression`), so no `options` object here
 * carries suppression and none should acquire one.
 *
 * ONE SHARED ACTION TOKEN, TWO MAPS. The trial modal's "Try on my own" reports
 * `[PRACTICE INVITE] skip` — the invite modal's token — so the trial map has an
 * entry for it and the package declares it once. That is why the two maps are not
 * disjoint, and why the suite asserts the shared entry explicitly rather than
 * leaving it to look like a copy-paste slip.
 */
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN } from '@/models/analytics/features/trialsAndUpgradesScreenNames';
import {
  PRACTICE_INVITE_ACTION,
  PRACTICE_TRIAL_ACTION,
  type PracticeInviteActionToken,
  type PracticeInviteAnalyticsPorts,
  type PracticeTrialActionToken,
  type PracticeTrialAnalyticsPorts,
  type TrackPracticeInviteClick,
  type TrackPracticeInviteViewed,
  type TrackPracticeTrialClick,
  type TrackPracticeTrialViewed,
} from '@heidi/commerce/contracts';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';

/** Snowplow-first, matching what the two views have always sent. */
const VIEW_TRACK_OPTIONS = { amplitude: false } as const;

const INVITE_ACTION_BY_TOKEN = {
  [PRACTICE_INVITE_ACTION.sendInvites]: ButtonAction.PracticeInviteSendInvites,
  [PRACTICE_INVITE_ACTION.skip]: ButtonAction.PracticeInviteSkip,
  [PRACTICE_INVITE_ACTION.close]: ButtonAction.PracticeInviteClose,
} as const satisfies Record<PracticeInviteActionToken, ButtonAction>;

const TRIAL_ACTION_BY_TOKEN = {
  [PRACTICE_TRIAL_ACTION.getStarted]: ButtonAction.PracticeTrialGetStarted,
  [PRACTICE_INVITE_ACTION.skip]: ButtonAction.PracticeInviteSkip,
} as const satisfies Record<PracticeTrialActionToken, ButtonAction>;

/** The invite modal became visible. Fired once per mount by the domain side. */
export const trackPracticeInviteViewed: TrackPracticeInviteViewed = ({ screen_name }) => {
  track({
    options: VIEW_TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: { screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name] },
  });
};

/** One of the invite modal's three CTAs was clicked. */
export const trackPracticeInviteClick: TrackPracticeInviteClick = ({
  screen_name,
  action,
  ...rest
}) => {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
      action: INVITE_ACTION_BY_TOKEN[action],
      ...rest,
    },
  });
};

/** The practice trial-start modal became visible. */
export const trackPracticeTrialViewed: TrackPracticeTrialViewed = ({ screen_name }) => {
  track({
    options: VIEW_TRACK_OPTIONS,
    event_name: TrackingEventName.ModalViewed,
    props: { screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name] },
  });
};

/** The practice trial modal's primary or secondary CTA was clicked. */
export const trackPracticeTrialClick: TrackPracticeTrialClick = ({
  screen_name,
  action,
  ...rest
}) => {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
      action: TRIAL_ACTION_BY_TOKEN[action],
      ...rest,
    },
  });
};

/**
 * Each surface's seams as one bundle.
 *
 * The invite bundle earns its keep: the view and two of the clicks live in the
 * lazily-imported content component while the dismiss lives in the shell, so the
 * bundle is what makes the pair one seam rather than two half-seams when the
 * surface moves behind the door.
 */
export const practiceInviteAnalyticsPorts: PracticeInviteAnalyticsPorts = {
  trackViewed: trackPracticeInviteViewed,
  trackClick: trackPracticeInviteClick,
};

export const practiceTrialAnalyticsPorts: PracticeTrialAnalyticsPorts = {
  trackViewed: trackPracticeTrialViewed,
  trackClick: trackPracticeTrialClick,
};
